'use client';

import React, {
  ReactNode,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import styles from './Popover.module.css';

type PopoverProps = {
  anchorEl: HTMLElement | null;
  open: boolean;
  children: ReactNode;
  offset?: number;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
};

type Position = { top: number; left: number } | null;

export function Popover({
  anchorEl,
  open,
  children,
  offset = 8,
  onMouseEnter,
  onMouseLeave,
}: PopoverProps) {
  const [mounted, setMounted] = useState(false);
  const [position, setPosition] = useState<Position>(null);
  const [placement, setPlacement] = useState<'below' | 'above'>('below');
  const [arrowLeft, setArrowLeft] = useState<number | null>(null);
  const popoverRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  const updatePosition = () => {
    if (!anchorEl || !popoverRef.current) {
      setPosition(null);
      return;
    }
    const rect = anchorEl.getBoundingClientRect();
    const pop = popoverRef.current;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    // Measure to get current size
    const popWidth = pop.offsetWidth;
    const popHeight = pop.offsetHeight;

    const spaceBelow = viewportHeight - rect.bottom;
    const spaceAbove = rect.top;
    const placeBelow =
      spaceBelow >= popHeight + offset || spaceBelow >= spaceAbove;

    const top = placeBelow
      ? rect.bottom + offset
      : rect.top - popHeight - offset;
    let left = rect.left + rect.width / 2 - popWidth / 2;

    // Clamp horizontally within viewport with margin.
    // Always keep a minimum padding from vertical edges
    const margin = viewportWidth <= 480 ? 8 : 12;
    left = Math.max(margin, Math.min(left, viewportWidth - popWidth - margin));

    setPosition({ top, left });
    setPlacement(placeBelow ? 'below' : 'above');

    // compute arrow left inside container so it points to icon center
    const iconCenterX = rect.left + rect.width / 2;
    const desiredArrowLeft = iconCenterX - left - 8.5; // center of 17px arrow
    // On mobile, allow arrow to be nearer to the edge like in the mock, but respect container padding
    const edgePadding = viewportWidth <= 480 ? 6 : 8;
    const minArrow = edgePadding;
    const maxArrow = popWidth - 17 - edgePadding;
    setArrowLeft(Math.max(minArrow, Math.min(desiredArrowLeft, maxArrow)));
  };

  useLayoutEffect(() => {
    if (!open) return;
    updatePosition();
    const onScroll = () => updatePosition();
    const onResize = () => updatePosition();
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize, true);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize, true);
    };
  }, [open, anchorEl]);

  if (!mounted || !open) return null;

  return createPortal(
    <div
      className={styles.popoverContainer}
      ref={popoverRef}
      style={{
        ...(position ?? {}),
        ...(arrowLeft != null
          ? { ['--arrow-left' as any]: `${arrowLeft}px` }
          : {}),
      }}
      data-placement={placement}
      role="tooltip"
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <div className={styles.arrow} data-placement={placement} aria-hidden>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="17"
          height="12"
          viewBox="0 0 17 12"
          fill="none"
        >
          <path
            d="M7.71679 0.486589C8.11715 -0.0177356 8.88285 -0.0177351 9.28321 0.486589L16.7388 9.87824C17.2591 10.5336 16.7924 11.5 15.9556 11.5H1.04437C0.207614 11.5 -0.259098 10.5336 0.261163 9.87824L7.71679 0.486589Z"
            fill="#F0F0F0"
          />
        </svg>
      </div>
      {children}
    </div>,
    document.body,
  );
}

export default Popover;
